"use client";

import React, { useEffect, useState } from "react";
import { Menu, X, Facebook, Instagram } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";

// Composant pour l'icône TikTok avec effet néon
function TiktokIcon({
  className = "size-5"
}: {
  className?: string;
}) {
  return <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" style={{
    filter: "drop-shadow(-1px -1px 0px #00f2fe) drop-shadow(1px 1px 0px #fe2c55)"
  }}>
      <path d="M12.525 2.25c.18 1.01.65 1.93 1.34 2.65a5.53 5.53 0 003.88 1.54V9a8.96 8.96 0 01-3.88-1.02V15a6.5 6.5 0 11-6.5-6.5c.29 0 .58.02.86.06V11.2a3.99 3.99 0 102.64 3.8V2.25h1.86z" />
    </svg>;
}
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="223278" className="hidden md:flex gap-6 items-center">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-20 bg-muted/40 animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu className="hidden md:flex">
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative px-2 py-2 text-sm font-semibold uppercase tracking-widest text-foreground/90 transition-colors hover:text-primary outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm">
                {item.label}
                <span className="absolute inset-x-0 bottom-0 h-[2px] scale-x-0 bg-primary transition-transform duration-300 ease-out group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-background/95 backdrop-blur-2xl border-b border-primary/20 shadow-2xl md:hidden flex flex-col py-6 px-6 gap-3 origin-top animate-in slide-in-from-top-2 fade-in duration-200">
      {loading ? <div className="flex flex-col gap-3">
          {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-10 w-full bg-muted/40 animate-pulse rounded" />)}
        </div> : menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="p-3 text-base font-semibold uppercase tracking-wider text-foreground hover:text-primary hover:bg-primary/10 rounded-md transition-all" onClick={() => setIsOpen(false)} data-index={i}>
            {item.label}
          </Link>)}
      
      {/* Réseaux sociaux colorés sur mobile */}
      <div className="pt-4 mt-2 border-t border-primary/20 flex items-center justify-center gap-6">
        <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="p-2 text-[#1877F2] hover:scale-110 transition-transform" aria-label="Facebook">
          <Facebook className="size-6 fill-[#1877F2] text-[#1877F2]" />
        </a>
        <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-gradient-to-tr from-[#f09433] via-[#e6683c] to-[#bc1888] text-white hover:scale-110 transition-transform shadow-md" aria-label="Instagram">
          <Instagram className="size-5" />
        </a>
        <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" className="p-2 text-white hover:scale-110 transition-transform" aria-label="TikTok">
          <TiktokIcon className="size-6" />
        </a>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="kickoff-events" className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "bg-background/90 backdrop-blur-md border-b border-primary/20 shadow-lg py-1" : "bg-background/60 backdrop-blur-sm border-b border-white/5 py-2"}`}>
      <div className="container mx-auto px-4 md:px-6 h-20 flex items-center justify-between gap-4">
        {/* LOGO */}
        <div className="flex-shrink-0 flex items-center">
          <Link to="/" className="flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md transition-transform hover:scale-105">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647492/img/image_whnn072y.png" className="w-auto h-14 md:h-16 object-contain" alt="Kickoff Events" />
          </Link>
        </div>

        {/* NAVIGATION DESKTOP */}
        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="46">
            <DesktopNav />
          </MenuProvider>
        </div>

        {/* ICÔNES RÉSEAUX SOCIAUX COLORÉES & MOBILE TOGGLE */}
        <div className="flex items-center justify-end gap-2 flex-shrink-0">
          <div className="hidden md:flex items-center gap-2">
            {/* Facebook Bleu */}
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="p-2 text-[#1877F2] hover:scale-110 hover:bg-white/10 rounded-full transition-all" aria-label="Facebook">
              <Facebook className="size-5 fill-[#1877F2] text-[#1877F2]" />
            </a>

            {/* Instagram Dégradé */}
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg bg-gradient-to-tr from-[#f09433] via-[#e6683c] to-[#bc1888] text-white hover:scale-110 shadow-md transition-all" aria-label="Instagram">
              <Instagram className="size-4" />
            </a>

            {/* TikTok Néon */}
            <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" className="p-2 text-white hover:scale-110 hover:bg-white/10 rounded-full transition-all" aria-label="TikTok">
              <TiktokIcon className="size-5" />
            </a>
          </div>

          {/* BOUTON MENU MOBILE */}
          <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-primary focus-visible:ring-2 focus-visible:ring-primary rounded-full" onClick={() => setIsMobileOpen(!isMobileOpen)} aria-label={isMobileOpen ? "Close menu" : "Open menu"}>
            {isMobileOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>
        </div>
      </div>

      {/* NAVIGATION MOBILE */}
      <MenuProvider menu_id="46">
        <MobileNav isOpen={isMobileOpen} setIsOpen={setIsMobileOpen} />
      </MenuProvider>
    </section>;
}